import React from 'react';
import PropTypes from 'prop-types';
import environmentNames from '../../../../qti/environmentNames';
import domService from '../../../../services/DomService';
import MultimediaStatusInfo from './MultimediaStatusInfo';
import PlayPauseButton from './PlayPauseButton';
import VolumeControl from './VolumeControl';
import multimediaEvents from './MultimediaEvents';
import multimediaCanPauseModes from './MultimediaDisplayConstants';

import sessionReader from '../../../../store/readHelpers/SessionReader';
import styles from './MultimediaDisplay.scss';
import utilService from '../../../../services/UtilService';
import interactionVisibilityService from '../../../../services/questionView/InteractionVisibilityService';
import VideoControlsOverlay from './VideoControlsOverlay';
import ServiceWorkerService from '../../../../services/ServiceWorkerService';
import multimediaService from '../../../../services/MultimediaService';
import stringUtils from '../../../../qti/utils/StringUtils';
import storeManager from '../../../../store/StoreManager';
import readinessCheckActions from '../../../../actions/ReadinessCheckActions';
import readinessCheckService from '../../../../services/ReadinessCheckService';
import marketplacePropertiesService from '../../../../services/MarketplacePropertiesService';

/**
 * Multimedia clip component.
 * Used for displaying multimedia clips inserted into questions.
 */
class MultimediaDisplay extends React.Component {
    static propTypes = {
        displayContext: PropTypes.string,
        isPreRender: PropTypes.bool,
        lockedMedia: PropTypes.bool,
        mediaElement: PropTypes.object,
        model: PropTypes.object.isRequired,
        orientation: PropTypes.string,
        reportReady: PropTypes.func,
        services: PropTypes.object,
        onMediaPlayedCounterReset: PropTypes.func,
        onMediaPlayedToCompletion: PropTypes.func,
    };

    static defaultProps = {
        reportReady: () => undefined,
    };

    constructor(props, context) {
        super(props, context);
        const timesPlayed = multimediaService.getStoredPlayCount(props.model);
        this.replayTimesUid = `replayTimes${stringUtils.getUID()}`;
        this.hasBeenStarted = timesPlayed > 0;

        this.state = {
            multimediaTimeText: '0:00 / 0:00',
            fullyLoaded: utilService.isIOS() || false, // preload auto not supported by IOS. Bypassing preload to make playback work
            replyTimesText: this.getReplyTimesText(Math.max((props.model.repeatTimes + 1) - timesPlayed, 0)),
            multimediaError: '',
        };

        this.onEnded = this.onEnded.bind(this);
        this.onLoadedMetadata = this.onLoadedMetadata.bind(this);
        this.onMediaPlayedToCompletion = this.onMediaPlayedToCompletion.bind(this);
        this.onMultimediaCanPlayThrough = this.onMultimediaCanPlayThrough.bind(this);
        this.onMultimediaProgress = this.onMultimediaProgress.bind(this);
        this.onPause = this.onPause.bind(this);
        this.onPlay = this.onPlay.bind(this);
        this.onStartPlaying = this.onStartPlaying.bind(this);
        this.onStopPlayback = this.onStopPlayback.bind(this);
        this.timeUpdate = this.timeUpdate.bind(this);

        const { WHEN_COMPLETED, WHEN_COMPLETED_AUTOPLAY } = interactionVisibilityService.visibilityValues;
        if ([WHEN_COMPLETED, WHEN_COMPLETED_AUTOPLAY].includes(this.props.model.interactionVisibility) && this.hasPlayedMaxRepetitions()) {
            this.showInteractionsIfApplicable();
        }
    }

    componentDidMount() {
        this.setBufferBarWidth(0);
        this.props.reportReady(true);

        if (this.props.isPreRender) {
            return;
        }
        if (sessionReader.isContextReadinessCheck()) {
            const positionValue = readinessCheckService.getPositionValue(this.multimedia.id);
            if (positionValue !== undefined) {
                this.multimedia.currentTime = positionValue;
            }
        }
        window.addEventListener(multimediaEvents.STOP_ALL_PLAYBACK, this.onStopPlayback);
        window.addEventListener(multimediaEvents.MEDIA_PLAYED_TO_COMPLETION, this.onMediaPlayedToCompletion);
        window.addEventListener(multimediaEvents.START_PLAYING, this.onStartPlaying);
        if (this.multimedia instanceof Element) {
            if (this.multimedia.readyState === HTMLMediaElement.HAVE_ENOUGH_DATA) {
                // 'canplaythrough' will already have fired.
                this.onMultimediaCanPlayThrough();
            } else {
                this.multimedia.addEventListener('canplaythrough', this.onMultimediaCanPlayThrough, false);
            }
            this.multimedia.addEventListener('loadedmetadata', this.onLoadedMetadata, false);
            this.multimedia.addEventListener('progress', this.onMultimediaProgress, false);
            this.multimedia.addEventListener('timeupdate', this.timeUpdate, false);
            this.multimedia.addEventListener('play', this.onPlay, false);
            this.multimedia.addEventListener('pause', this.onPause, false);
            this.multimedia.addEventListener('ended', this.onEnded, false);
        }
    }

    // eslint-disable-next-line camelcase,react/sort-comp
    UNSAFE_componentWillUpdate(nextProps, nextState) {
        const { stateCacheService } = this.props.services;
        let multimediaCurrentTime;
        const resumeMultimediaKey = multimediaService.getResumeMultimediaKey(this.props.model);

        if (nextState.fullyLoaded) {
            multimediaCurrentTime = parseFloat(stateCacheService.getQuestionCacheValue(resumeMultimediaKey));

            if (!isNaN(multimediaCurrentTime) && this.multimedia) {
                // delete stored key
                stateCacheService.removeQuestionCacheValue(resumeMultimediaKey);
                this.multimedia.currentTime = multimediaCurrentTime;
                this.timeUpdate();
            }
        }
    }

    componentWillUnmount() {
        const { displayContext, services, model } = this.props;

        if (displayContext === 'child-desc-document') {
            // while video is not yet loaded the duration is NaN
            this.multimedia.currentTime = this.multimedia.duration || 0;
        }

        if (this.state.playing || this.multimedia.currentTime > 0) {
            services.stateCacheService.setQuestionCacheValue(multimediaService.getResumeMultimediaKey(model), this.multimedia.currentTime);
        }

        if (this.multimedia instanceof Element) {
            this.multimedia.removeEventListener('loadedmetadata', this.onLoadedMetadata);
            this.multimedia.removeEventListener('canplaythrough', this.onMultimediaCanPlayThrough);
            this.multimedia.removeEventListener('progress', this.onMultimediaProgress);
            this.multimedia.removeEventListener('timeupdate', this.timeUpdate);
            this.multimedia.removeEventListener('ended', this.onEnded);
            this.multimedia.removeEventListener('play', this.onPlay);
            this.multimedia.removeEventListener('pause', this.onPause);
        }

        window.removeEventListener(multimediaEvents.STOP_ALL_PLAYBACK, this.onStopPlayback);
        window.removeEventListener(multimediaEvents.MEDIA_PLAYED_TO_COMPLETION, this.onMediaPlayedToCompletion);
        window.removeEventListener(multimediaEvents.START_PLAYING, this.onStartPlaying);
        if (!this.props.isPreRender) {
            this.unLockNavigationIfNeeded();
        }
    }

    onMultimediaCanPlayThrough() {
        // IE does not fire 'progress' event on short multimedia files (e.g: 3 sec)
        // => manually call onMultimediaProgress() to update buffer bar
        this.onMultimediaProgress();
        this.setTimeText();
        this.setState({
            fullyLoaded: true,
        });
    }

    timeUpdate() {
        this.setTimeText();

        // Fills out the slider with the appropriate position.
        const percentageOfSong = (this.multimedia.currentTime / this.multimedia.duration);

        // Updates the track progress div.
        if (this.playhead) {
            this.playhead.style.width = `${percentageOfSong.toFixed(3) * 100}%`;
        }
    }

    /**
     * Sets buffer bar width in order to show percent of preloading of multimedia
     * @param newWidth
     */
    setBufferBarWidth = (newWidth) => {
        if (this.bufferBarProgress) {
            this.bufferBarProgress.style.width = newWidth;
        }
    };

    onEnded = () => {
        if (sessionReader.isContextReadinessCheck()) {
            storeManager.dispatch(
                readinessCheckActions.setReadinessCheckPosition(this.multimedia.id, 0)
            );
        }

        if (!this.hasPlayedMaxRepetitions()) {
            this.multimedia.pause();
        }
        this.hasBeenStarted = false;
        this.setState({
            playing: false,
        }, () => {
            this.unLockNavigationIfNeeded();
            this.showInteractionsIfApplicable();
        });

        window.dispatchEvent(
            new CustomEvent(
                multimediaEvents.MEDIA_PLAYED_TO_COMPLETION,
                { detail: { logicalName: this.props.model.logicalName } },
            )
        );
    };

    onPlay() {
        if (sessionReader.isContextReadinessCheck()) {
            const positionValue = readinessCheckService.getPositionValue(this.multimedia.id);
            if (positionValue) {
                this.multimedia.currentTime = positionValue;
            }
        }

        if (!this.playTriggeredByUs) {
            const timeNow = this.multimedia.currentTime;
            this.pauseTriggeredByUs = true;
            this.multimedia.pause();
            this.multimedia.currentTime = timeNow;
        }
        this.playTriggeredByUs = false;
    }

    onPause() {
        if (
            !this.pauseTriggeredByUs
            && this.multimedia.currentTime < this.multimedia.duration
        ) {
            this.playTriggeredByUs = true;
            this.multimedia.play();
        }
        this.pauseTriggeredByUs = false;
    }

    onLoadedMetadata() {
        if (this.multimedia && this.props.model.type === 'video') {
            this.setState({
                nativeVideoWidth: this.multimedia.videoWidth,
            });
        }
    }

    onMultimediaProgress() {
        let percent = 0;
        const bufferedEnd = this.multimedia.buffered.length > 0 ? this.multimedia.buffered.end(this.multimedia.buffered.length - 1) : 0;
        const duration = this.multimedia.duration;

        if (duration > 0) {
            percent = (bufferedEnd / duration);
        }

        this.setBufferBarWidth(`${percent * 100}%`);
    }

    getReplyTimesText = (timesRemaining) => {
        const { lockedMedia, services: { translationService: { t } } } = this.props;

        if (lockedMedia) {
            return t('audio.noRepliesRestricted');
        }

        switch (timesRemaining) {
        case 0:
            return t('audio.noRepliesRestricted');
        case 1:
            return t('audio.lastReplyRestricted');
        default:
            return t('audio.replyTimesRestricted', { times: timesRemaining });
        }
    }

    getTimelineWidth = () => {
        let timelineWidth = 0;

        if (this.timeline?.offsetWidth > 0) {
            timelineWidth = this.timeline.offsetWidth;
        } else if (this.timeline && this.timeline.offsetParent && this.timeline.offsetParent.offsetWidth) {
            timelineWidth = this.timeline.offsetParent.offsetWidth - 100;
        }

        return timelineWidth;
    };

    /**
     * Sets the current multimedia location compared to the multimedia duration,
     * based on the this.multimedia object
     */
    setTimeText = () => {
        const multimedia = this.multimedia;
        const currentSeconds = (Math.floor(multimedia.currentTime % 60) < 10 ? '0' : '') + Math.floor(multimedia.currentTime % 60);
        const currentMinutes = Math.floor(multimedia.currentTime / 60);
        const multimediaTimeTextCurrent = `${currentMinutes}:${currentSeconds}`;
        const multimediaTimeTextDuration =
            `${Math.floor(multimedia.duration / 60)}:` +
            `${(Math.floor(multimedia.duration % 60) < 10 ? '0' : '')}${Math.floor(multimedia.duration % 60)}`;
        const multimediaTimeText = `${multimediaTimeTextCurrent} / ${multimediaTimeTextDuration}`;

        this.setState({
            multimediaTimeText,
        });
    };

    dispatchStopAllPlaybackEvent = () => {
        window.dispatchEvent(
            new CustomEvent(
                multimediaEvents.STOP_ALL_PLAYBACK,
                { detail: { triggeredBy: this.props.model.logicalName } }
            )
        );
    };

    hasPlayedMaxRepetitions = () => {
        const { repeatTimes } = this.props.model;
        let hasPlayedMaxRepetitions = false;
        let storedPlayCount;
        const currentPlayComplete = this.multimedia ?
            (this.multimedia.currentTime === 0 || this.multimedia.currentTime === this.multimedia.duration)
            : true;

        if (repeatTimes !== -1) {
            try {
                storedPlayCount = multimediaService.getStoredPlayCount(this.props.model);
                // check how many times the file has be played before in SEB
                hasPlayedMaxRepetitions = (storedPlayCount > repeatTimes);
            } catch (error) {
                // Swallow error
            }
        }

        return hasPlayedMaxRepetitions && currentPlayComplete;
    };

    updateTimesPlayed = (incrementStorageValue = false) => {
        const model = this.props.model;
        const timesPlayed = multimediaService.updateTimesPlayed(model, incrementStorageValue);

        this.setState({
            replyTimesText: this.getReplyTimesText(Math.max((model.repeatTimes + 1) - timesPlayed, 0)),
        });
    };

    onStartPlaying = (event) => {
        if (
            event.detail.logicalName === this.props.model.logicalName
            && this.multimedia instanceof Element
        ) {
            if (event.detail.fromStart === true) {
                this.multimedia.currentTime = 0;
            }
            this.onPlayBtnClick();
        }
    };

    onPlayBtnClick = () => {
        if (this.isNavigationLocked() || !this.state.fullyLoaded || !(this.multimedia instanceof Element)) {
            return;
        }

        if (this.multimedia.ended || !this.hasBeenStarted) {
            this.multimedia.currentTime = 0;
        }

        /**
         * increment repetition count only: if playhead is at 0 not every pause/play during track
         * and not having unlimited allowed repetitions
         */
        if ((this.props.model.repeatTimes !== -1) && this.multimedia.currentTime === 0) {
            // a restricted file is being played in SEB.
            // store in stateCacheService the number of times that it has been played
            try {
                this.updateTimesPlayed(true);
            } catch (error) {
                // Swallow error
            }
        }

        // stopping ongoing clips before playing
        this.dispatchStopAllPlaybackEvent();
        this.hasBeenStarted = true;
        this.setState({
            playing: true,
        }, () => {
            this.lockNavigationIfNeeded();
            this.playTriggeredByUs = true;
            this.multimedia.play();
        });
    };

    onPauseBtnClick = () => {
        const pauseMode = marketplacePropertiesService.isEnableMediaPauseModeOn() ?
            this.isPausingBasicMode() : this.props.model.canPause;
        if (
            pauseMode
            && this.multimedia instanceof Element
            && !this.hasPlayedMaxRepetitions()
        ) {
            this.setState({
                playing: false,
            }, () => {
                this.playTriggeredByUs = false;
                this.pauseTriggeredByUs = true;
                this.multimedia.pause();
            });
        }
    };

    onStopPlayback = () => {
        if (this.multimedia.currentTime > 0 && this.multimedia.currentTime < this.multimedia.duration && sessionReader.isContextReadinessCheck()) {
            storeManager.dispatch(
                readinessCheckActions.setReadinessCheckPosition(this.multimedia.id, this.multimedia.currentTime)
            );
        }
        if (!(this.multimedia instanceof Element)) {
            return;
        }

        if (
            this.state.playing === true
        ) {
            this.setState({
                playing: false,
            }, () => {
                this.playTriggeredByUs = false;
                this.pauseTriggeredByUs = true;
                this.multimedia.pause();
                this.unLockNavigationIfNeeded();
            });
        }

        // We need this because we will not receive any new props, and state might not change.
        this.forceUpdate();
    };

    onTimelineClick = (e) => {
        if (!(this.multimedia instanceof Element) || !this.state.fullyLoaded) {
            return;
        }

        const offsetX = domService.getMouseOffsetInsideElement(e).x;
        const percentage = offsetX / (this.getTimelineWidth());
        // Sets the song location with the percentage.
        this.multimedia.currentTime = this.multimedia.duration * percentage;
    };

    onMediaPlayedToCompletion = () => {
        this.forceUpdate();
        if (this.props.onMediaPlayedToCompletion) {
            this.props.onMediaPlayedToCompletion();
        }
    };

    resetPlayedTimes = () => {
        const { stateCacheService } = this.props.services;
        const assessmentRunId = sessionReader.getAssessmentRunId();
        const userId = sessionReader.getUserId();
        stateCacheService.removeQuestionCacheValue(multimediaService.getStorageKey(this.props.model, assessmentRunId, userId));
        this.multimedia.currentTime = 0;
        this.timeUpdate();
        this.updateTimesPlayed();
        if (this.props.onMediaPlayedCounterReset) {
            this.props.onMediaPlayedCounterReset();
        }
    };

    /**
     * Allow seek = canPause && unlimited repetitions
     * Negating this means "multimedia clip is restricted"
     */
    isSeekAllowed = () => {
        const { canPause, repeatTimes } = this.props.model;
        return (repeatTimes === -1) && (marketplacePropertiesService.isEnableMediaPauseModeOn() ?
            this.isPausingBasicMode() : canPause);
    };

    isRestrictedMultimedia = () => {
        const { canPauseMode, canPause } = this.props.model;
        return marketplacePropertiesService.isEnableMediaPauseModeOn() ? canPauseMode !== multimediaCanPauseModes.BASIC : !canPause;
    };

    mediaLockNavigationStatus = (lockValue) => {
        const { id, canPauseMode } = this.props.model;
        this.props.services.interactionService.setMultimediaStatus(lockValue, id, canPauseMode);
    }

    lockNavigationIfNeeded = () => {
        if (this.isRestrictedMultimedia()) {
            this.mediaLockNavigationStatus(true);
        }
    };

    unLockNavigationIfNeeded = () => {
        if (this.isRestrictedMultimedia()) {
            this.mediaLockNavigationStatus(false);
        }
    };

    showInteractionsIfApplicable = () => {
        const mediaModel = this.props.model;
        interactionVisibilityService.showInteractionsInQuestion(mediaModel.questionId, mediaModel.logicalName);
    };

    isNavigationLocked = () => {
        const { interactionService } = this.props.services;
        return (typeof interactionService.getActiveMultimediaIdentifier() !== 'undefined');
    };

    isPausingBasicMode = () => {
        const { canPauseMode } = this.props.model;
        return canPauseMode === multimediaCanPauseModes.BASIC;
    };

    getMultimediaUrl = () => {
        if (process.env.NODE_ENV === environmentNames.DEVELOPMENT) {
            this.props.model.url = 'files/content_item_1357554_1955851.mp3';
            return 'files/content_item_1357554_1955851.mp3';
        }
        return this.props.model.url;
    };

    getSkinToUse = (skinFromModel, displayContext) => {
        if (displayContext === 'choiceOption') {
            return 'basic';
        }

        return skinFromModel;
    };

    onContextMenu = (e) => {
        e.preventDefault();
        return false;
    };

    renderResetLink = (enableResetPlayedTimes) => {
        if (enableResetPlayedTimes) {
            return (<button className="resetPlayedTimes below" onClick={this.resetPlayedTimes}>
                {this.props.services.translationService.t('audio.resetPlayedTimes')}
            </button>);
        }

        return null;
    };

    renderSource = (mediaModel) => {
        return <source key={mediaModel.url} src={mediaModel.url} type={mediaModel.mimeType} />;
    };

    renderMedia = () => {
        const { isPreRender, mediaElement, model } = this.props;
        const { logicalName, type, width, sources, url } = model;
        const preload = utilService.isIOS() ? 'metadata' : 'auto';
        const id = `media-${logicalName}${isPreRender ? '-preRender' : ''}`;
        const extraProps = {};
        if (ServiceWorkerService.browserSupportsServiceWorker()) {
            extraProps.crossOrigin = 'anonymous';
        }

        if (mediaElement instanceof HTMLMediaElement) {
            /**
             * The actual mediaElement has already been rendered at a higher level, most likely to enable preparing it for auto play
             * before the interaction it belongs in is rendered.
             */
            this.multimedia = mediaElement;
            return null;
        }

        if (type === 'video') {
            const style = {
                width: utilService.getIntOr(width, this.state.nativeVideoWidth), // React appends px.
            };

            return (
                /* eslint-disable-next-line jsx-a11y/media-has-caption */
                <video
                    {...extraProps}
                    id={id}
                    onContextMenu={this.onContextMenu}
                    preload={preload}
                    ref={(multimedia) => { this.multimedia = multimedia; }}
                    style={style}
                >
                    {
                        sources
                            ? sources.map(this.renderSource)
                            : null
                    }
                </video>
            );
        }

        return (
            /* eslint-disable-next-line jsx-a11y/media-has-caption */
            <audio
                {...extraProps}
                id={id}
                ref={(multimedia) => { this.multimedia = multimedia; }}
                preload={preload}
            >
                {
                    sources
                        ? sources.map(this.renderSource)
                        : null
                }
                {
                    url
                        ? <source src={url} />
                        : null
                }
            </audio>
        );
    };

    renderVideoWithBasicSkin = (btnProps, enableResetPlayedTimes) => {
        return (
            <React.Fragment>
                <div className="video-basic-skin-wrapper">
                    <div className="video-basic-skin-inner-wrapper">
                        {
                            this.renderMedia()
                        }
                        <VideoControlsOverlay {...btnProps} />
                    </div>
                    <div className="ia-multimediaplayer-controls skin-basic video">
                        <div
                            className="timeline skin-basic video"
                            ref={(timeline) => { this.timeline = timeline; }}
                        >
                            <div className="playhead" ref={(playhead) => { this.playhead = playhead; }} />
                        </div>
                    </div>
                </div>
                {
                    this.renderResetLink(enableResetPlayedTimes)
                }
            </React.Fragment>
        );
    };

    renderAudioWithBasicSkin = (btnProps, enableResetPlayedTimes) => {
        return (
            <React.Fragment>
                <div className="audio-basic-skin-wrapper">
                    {
                        this.renderMedia()
                    }
                    <div className="ia-multimediaplayer-controls skin-basic audio">
                        <PlayPauseButton
                            {...btnProps}
                        />
                        <div
                            className="timeline skin-basic audio"
                            ref={(timeline) => { this.timeline = timeline; }}
                        >
                            <div className="playhead" ref={(playhead) => { this.playhead = playhead; }} />
                        </div>
                    </div>
                </div>
                {
                    this.renderResetLink(enableResetPlayedTimes)
                }
            </React.Fragment>
        );
    };

    renderWithBasicSkin = (type, btnProps, enableResetPlayedTimes) => {
        if (type === 'video') {
            return this.renderVideoWithBasicSkin(btnProps, enableResetPlayedTimes);
        }

        return this.renderAudioWithBasicSkin(btnProps, enableResetPlayedTimes);
    };

    getTimelineAttributes = () => {
        const attributes = {
            className: 'timeline',
            ref: (timeline) => { this.timeline = timeline; },
        };

        if (this.isSeekAllowed()) {
            attributes.onClick = this.onTimelineClick;
            attributes.role = 'button';
        }

        return attributes;
    };

    renderWithDefaultSkin = (type, btnProps, enableResetPlayedTimes, canPause, canPauseMode, repeatTimes) => {
        return (<React.Fragment>
            {
                this.renderMedia()
            }

            <div className="ia-multimediaplayer-controls">
                <PlayPauseButton
                    {...btnProps}
                    describedById={this.replayTimesUid}
                />
                <React.Fragment>
                    <div className="songTime">{this.state.multimediaTimeText}</div>
                    <MultimediaStatusInfo
                        multimediaError={this.state.multimediaError}
                        canPause={canPause}
                        canPauseMode={canPauseMode}
                        repeatTimes={repeatTimes}
                        replyTimesText={this.state.replyTimesText}
                        enableResetPlayedTimes={enableResetPlayedTimes}
                        resetPlayedTimes={this.resetPlayedTimes}
                        translationService={this.props.services.translationService}
                        replayTimesUid={this.replayTimesUid}
                    />
                    <div className="bufferBar">
                        <div className="bufferBarProgress" ref={(bufferBar) => { this.bufferBarProgress = bufferBar; }} />
                    </div>


                    <div {...this.getTimelineAttributes()} >
                        <div className="playhead" ref={(playhead) => { this.playhead = playhead; }} />
                    </div>

                    <VolumeControl multimedia={this.multimedia} services={this.props.services} />
                </React.Fragment>
            </div>
        </React.Fragment>);
    };

    render() {
        const { model, services, displayContext, lockedMedia } = this.props;
        const { canPause, canPauseMode, prompt, repeatTimes, type } = model;
        const { fullyLoaded, playing } = this.state;
        const isLocked = this.isNavigationLocked() || this.hasPlayedMaxRepetitions() || lockedMedia;
        const skinToUse = this.getSkinToUse(model.skin, displayContext);
        const btnProps = {
            action: undefined,
            canPreload: !utilService.isIOS(),
            fullyLoaded,
            isLocked,
            onClick: undefined,
            playing,
        };
        const isPausingAllowed = marketplacePropertiesService.isEnableMediaPauseModeOn() ? this.isPausingBasicMode() : canPause;

        if (!isLocked && !playing) {
            btnProps.onClick = this.onPlayBtnClick;
            btnProps.action = 'play';
        } else if (playing && isPausingAllowed && !isLocked) {
            btnProps.onClick = this.onPauseBtnClick;
            btnProps.action = 'pause';
        }
        const canResetMultimediaPlayedTimes = services.interactionService.canResetMultimediaPlayedTimes();
        const enableResetPlayedTimes = !playing && canResetMultimediaPlayedTimes && this.hasPlayedMaxRepetitions();
        let wrapperClassName = `${styles['ia-multimedia-wrapper']} ${model.objectClass} ${model.type} skin-${skinToUse}`;
        if (enableResetPlayedTimes) {
            wrapperClassName += ' can-reset-played-times';
        }

        return (
            <div>
                {
                    prompt && prompt.length ? <p className={styles.mediaPrompt}>{prompt}</p> : null
                }
                <div className={wrapperClassName}>
                    {
                        skinToUse === 'basic'
                            ? this.renderWithBasicSkin(type, btnProps, enableResetPlayedTimes)
                            : this.renderWithDefaultSkin(type, btnProps, enableResetPlayedTimes, canPause, canPauseMode, repeatTimes)
                    }
                </div>
            </div>
        );
    }
}

export default MultimediaDisplay;
